import {
  mergeApiScopeRequirements,
  mergePermissionScopes,
} from 'utils/permissions/mergeAccessRestrictions';
import type { MenuTreeNode, UrlListItem } from './menuTree';

const mergeUrlLists = (
  a: UrlListItem[] | undefined,
  b: UrlListItem[] | undefined,
): UrlListItem[] | undefined => {
  if (!a || !b) {
    return a ?? b;
  }
  // The first item is the navigation target, so the original order is preserved.
  return [...new Set([...a, ...b])];
};

const mergeMenuTreeNodes = (
  existing: MenuTreeNode,
  other: MenuTreeNode,
): MenuTreeNode => {
  if (
    existing.restrictByRoute &&
    other.restrictByRoute &&
    existing.restrictByRoute !== other.restrictByRoute
  ) {
    throw new Error(
      `Cannot merge menu item "${existing.label}": ambiguous restrictByRoute "${existing.restrictByRoute}" and "${other.restrictByRoute}".`,
    );
  }

  // _key will be merged if possible, as it seems to be defined only manually / outside of OAS.
  // However if both nodes have a _key and they are different, it is ambiguous and we throw an error.
  if (existing._key && other._key && existing._key !== other._key) {
    throw new Error(
      `Cannot merge menu item "${existing.label}": ambiguous _key "${existing._key}" and "${other._key}".`,
    );
  }

  // Order matters: Unhandled properties from `existing` take precedence over `other`.
  const merged: MenuTreeNode = { ...other, ...existing };

  const apiScopeRequirement = mergeApiScopeRequirements(
    existing.apiScopeRequirement,
    other.apiScopeRequirement,
  );
  if (apiScopeRequirement !== undefined) {
    merged.apiScopeRequirement = apiScopeRequirement;
  }

  const permissions = mergePermissionScopes(
    existing.permissions,
    other.permissions,
  );
  if (permissions !== undefined) {
    merged.permissions = permissions;
  }

  const urlList = mergeUrlLists(existing.urlList, other.urlList);
  if (urlList !== undefined) {
    merged.urlList = urlList;
  }

  if (existing.children && other.children) {
    merged.children = mergeMenuTree([existing.children, other.children]);
  }

  return merged;
};

export const mergeMenuTree = (
  nodes: [MenuTreeNode[], MenuTreeNode[]],
): MenuTreeNode[] => {
  const [existingNodes, otherNodes] = nodes;
  const mergedNodes: MenuTreeNode[] = [...existingNodes];

  for (const node of otherNodes) {
    // The menu is a visual tree, so items are matched by their user-facing label;
    // `_key` cannot be used because items generated from the OAS do not have one.
    const existingNode = mergedNodes.find(
      (mergedNode) => mergedNode.label === node.label,
    );

    if (existingNode === undefined) {
      mergedNodes.push(node);
    } else {
      mergedNodes[mergedNodes.indexOf(existingNode)] = mergeMenuTreeNodes(
        existingNode,
        node,
      );
    }
  }

  return mergedNodes;
};
